/**
 * Native push payload → in-app destination.
 *
 * Reads the `data` object OneSignal delivers (see supabase/functions/send-push).
 * Returns the most specific supported destination, or `null` when the payload
 * has no usable metadata — callers should then just open the app normally.
 */

import { concertUrl, isUUID } from '@/lib/entityLinks';
import { getTourLink, getArtistLink } from '@/lib/linkUtils';

export type PushData = Record<string, any> | null | undefined;

export const resolvePushDestination = (data: PushData): string | null => {
  if (!data || typeof data !== 'object') return null;

  const type = typeof data.type === 'string' ? data.type : '';
  const reviewId = data.review_id;
  const listId = data.list_id;
  const username = data.actor_username || data.username;

  // Explicit deep link wins when the producer supplies one.
  if (typeof data.path === 'string' && data.path.startsWith('/')) return data.path;

  switch (type) {
    case 'review_like':
    case 'comment_on_review':
    case 'comment_reply':
    case 'mention':
      if (isUUID(reviewId)) return `/reviews/${reviewId}`;
      if (isUUID(listId)) return `/lists/${listId}`;
      return null;

    case 'list_like':
      return isUUID(listId) ? `/lists/${listId}` : null;

    case 'new_follower':
    case 'follow_request_approved':
      return username && username !== 'unknown' ? `/profile/${username}` : null;

    case 'tour_announced':
      if (data.tour_slug || data.tour_id || data.tour_name) {
        return getTourLink(data.tour_id, data.tour_name, data.tour_slug);
      }
      if (data.artist_slug || data.artist_id || data.artist_name) {
        return getArtistLink(data.artist_id, data.artist_name, data.artist_slug);
      }
      return null;

    case 'artist_nearby_show':
    case 'concert_announced':
      if (data.concert_slug || isUUID(data.concert_id)) {
        return concertUrl({ slug: data.concert_slug, id: data.concert_id });
      }
      if (data.artist_slug || data.artist_id || data.artist_name) {
        return getArtistLink(data.artist_id, data.artist_name, data.artist_slug);
      }
      return null;

    default:
      // Unknown type: still honour unambiguous entity metadata.
      if (isUUID(reviewId)) return `/reviews/${reviewId}`;
      if (isUUID(listId)) return `/lists/${listId}`;
      return null;
  }
};
